// Rows with several views (AMF-23106): one layer row, one WFS, and a dropdown
// that swaps the style. The lane-restriction rows used to be one row per
// flavour, each with its own slug, so the old flavour slugs live on here as
// (row, view) references and keep shared links working.
//
// Pure on purpose — URLStateManager imports ol/* and reads window.location at
// load, so the parsing it delegates here is what the node tests can reach.
import { resolveLayerSlug } from "./legacyLayerSlugs.mjs";

// Dropdown order. Also the tie-break when an old link named two flavours of
// the same row: the first one in this list wins.
export const VIEW_IDS = ["restrictions", "lanes", "lane-count"];

export const VIEW_LABELS = {
  restrictions: "Restrictions",
  lanes: "Lanes",
  "lane-count": "Lane count",
};

export const DEFAULT_VIEW = "restrictions";

// The HOV and Vehicle Access views merged into Restrictions; `views=` links that
// still name them open the merged view.
const RETIRED_VIEW_IDS = {
  hov: "restrictions",
  "vehicle-access": "restrictions",
};

const ref = (slug, view) => ({ slug, view });

// Retired flavour slug → the row and view that replaced it. Includes the
// pre-AMF-22763 `…-prod-…` forms, which used to alias to the flavour slugs in
// LEGACY_LAYER_SLUGS; they moved here so nothing resolves in two hops.
export const LEGACY_VIEW_REFS = {
  "rm-lane-restrictions-21620-hov": ref(
    "rm-lane-restrictions-21620",
    "restrictions",
  ),
  "rm-lane-restrictions-21620-vehicle-access": ref(
    "rm-lane-restrictions-21620",
    "restrictions",
  ),
  "rm-lane-restrictions-prod-21620-hov": ref(
    "rm-lane-restrictions-21620",
    "restrictions",
  ),
  "rm-lane-restrictions-prod-21620-vehicle-access": ref(
    "rm-lane-restrictions-21620",
    "restrictions",
  ),
  "rm-lane-restrictions-86014-hov": ref(
    "rm-lane-restrictions-86014",
    "restrictions",
  ),
  "rm-lane-restrictions-dev-26744-hov": ref(
    "rm-lane-restrictions-dev-26744",
    "restrictions",
  ),
  "rm-lane-restrictions-dev-26744-vehicle-access": ref(
    "rm-lane-restrictions-dev-26744",
    "restrictions",
  ),
  "osm-lane-restrictions-28241-hov": ref(
    "osm-lane-restrictions-28241",
    "restrictions",
  ),
  "osm-lane-restrictions-28241-vehicle-access": ref(
    "osm-lane-restrictions-28241",
    "restrictions",
  ),
  "osm-lane-restrictions-28241-lanes": ref(
    "osm-lane-restrictions-28241",
    "lanes",
  ),
  "osm-lane-restrictions-prod-28241-hov": ref(
    "osm-lane-restrictions-28241",
    "restrictions",
  ),
  "osm-lane-restrictions-prod-28241-vehicle-access": ref(
    "osm-lane-restrictions-28241",
    "restrictions",
  ),
  "osm-lane-restrictions-prod-28241-lanes": ref(
    "osm-lane-restrictions-28241",
    "lanes",
  ),
  "osm-lane-restrictions-dev-14906-hov": ref(
    "osm-lane-restrictions-dev-14906",
    "restrictions",
  ),
  "osm-lane-restrictions-dev-14906-vehicle-access": ref(
    "osm-lane-restrictions-dev-14906",
    "restrictions",
  ),
  "osm-lane-restrictions-dev-14906-lanes": ref(
    "osm-lane-restrictions-dev-14906",
    "lanes",
  ),
};

// `{ slug, view }` for any slug a URL may carry. `view` is null unless the slug
// is a retired flavour slug.
export function resolveLayerRef(slug) {
  return Object.hasOwn(LEGACY_VIEW_REFS, slug)
    ? LEGACY_VIEW_REFS[slug]
    : ref(resolveLayerSlug(slug), null);
}

// The view a row should open in: the requested one if the row offers it,
// otherwise its default.
export function pickView(offered, requested, fallback) {
  return offered.includes(requested) ? requested : fallback;
}

const entries = (raw) => (raw || "").split(",").filter(Boolean);

function firstInOrder(views) {
  return VIEW_IDS.find((v) => views.has(v));
}

/**
 * Parses `layers=`, `views=` and `revisions=` together, because a retired
 * flavour slug in either `layers=` or `revisions=` decides a row's view, and
 * the view decides which of that row's revisions survive.
 *
 * @param {{ layers?: string|null, views?: string|null, revisions?: string|null }} raw
 * @returns {{ layers: string[], views: Map<string, string>,
 *   revisions: Map<string, Array<{layerId: number, revision: number}>> }}
 */
export function parseLayerParams({ layers, views, revisions } = {}) {
  const implied = new Map(); // row → Set of flavours named by legacy slugs
  const imply = ({ slug, view }) => {
    if (!view) return;
    if (!implied.has(slug)) implied.set(slug, new Set());
    implied.get(slug).add(view);
  };

  const layerRefs = entries(layers).map(resolveLayerRef);
  layerRefs.forEach(imply);

  const revisionRefs = [];
  entries(revisions).forEach((entry) => {
    const tildeIdx = entry.lastIndexOf("~");
    if (tildeIdx === -1) return;
    const [layerId, revision] = entry
      .substring(tildeIdx + 1)
      .split(":")
      .map(Number);
    if (isNaN(layerId) || isNaN(revision)) return;
    const r = resolveLayerRef(entry.substring(0, tildeIdx));
    imply(r);
    revisionRefs.push({ ...r, layerId, revision });
  });

  const resolvedViews = new Map();
  implied.forEach((flavours, slug) =>
    resolvedViews.set(slug, firstInOrder(flavours)),
  );
  entries(views).forEach((entry) => {
    const parts = entry.split("~");
    const [slug, raw] = parts;
    const view = Object.hasOwn(RETIRED_VIEW_IDS, raw)
      ? RETIRED_VIEW_IDS[raw]
      : raw;
    if (parts.length === 2 && slug && VIEW_IDS.includes(view)) {
      resolvedViews.set(slug, view);
    }
  });

  const resolvedRevisions = new Map();
  revisionRefs.forEach(({ slug, view, layerId, revision }) => {
    // A flavour that lost the tie-break would pin revisions onto a view
    // that isn't shown.
    if (view && resolvedViews.get(slug) !== view) return;
    if (!resolvedRevisions.has(slug)) resolvedRevisions.set(slug, []);
    // Flavours that merged into one view (HOV + Vehicle Access) can both pin the
    // same layer; the first one in the URL wins.
    const pinned = resolvedRevisions.get(slug);
    if (pinned.some((r) => r.layerId === layerId)) return;
    pinned.push({ layerId, revision });
  });

  return {
    layers: [...new Set(layerRefs.map((r) => r.slug))],
    views: resolvedViews,
    revisions: resolvedRevisions,
  };
}

/** @param {Array<[string, string]>} rowViews [rowSlug, viewId] pairs */
export function serializeViews(rowViews) {
  return rowViews.map(([slug, view]) => `${slug}~${view}`).join(",");
}
